import React from "react";
import DefaultButton from "@components/Button";
import Icon from "@components/Icon";

type CardVariant = "default" | "static" | "selectable";

interface CardProps {
    iconName: string;
    title: string;
    buttonLabel?: string;
    onButtonClick: () => void;

    iconColorClass?: string;
    iconSize?: number;

    isButtonDisabled?: boolean;
    isBorderless?: boolean;
    variant?: CardVariant;

    accentColorClass?: string; 
}

const Card: React.FC<CardProps> = ({
    iconName,
    title,
    buttonLabel = "Choose",
    onButtonClick,

    iconColorClass = "",
    iconSize = 24,

    isButtonDisabled = false,
    isBorderless = false,
    variant = "default",

    accentColorClass = "",
}) => {
    const isStatic = variant === "static";
    const isSelectable = variant === "selectable";

    return (
        <div
            className={`
                relative flex flex-col items-center bg-white
                ${isSelectable ? "cursor-pointer h-full w-full justify-center" : "px-4 py-6"}
                ${isStatic && "cursor-pointer"}
                rounded-md border border-dark-20     
                transition-transform transition-shadow transition-colors
                duration-200 ease-out
                hover:scale-[1.02]
                hover:shadow-lg
                hover:border-dark-30
            `}
            onClick={(isStatic || isSelectable) ? onButtonClick : undefined}
            role={(isStatic || isSelectable) ? "button" : undefined}
            tabIndex={(isStatic || isSelectable) ? 0 : undefined}
        >
            <div className="flex flex-col items-center justify-center gap-2 text-center">
                {iconName && (
                    <div
                        className={`flex items-center justify-center bg-white
                            ${isBorderless ? "" : "rounded-md border border-[#E9E9E9]"}
                        `}
                        style={{
                            width: iconSize + 20,
                            height: iconSize + 20,
                        }}
                    >
                        <Icon
                            name={iconName}
                            width={iconSize}
                            height={iconSize}
                            className={iconColorClass}
                        />
                    </div>
                )}

                <h3
                    className={
                        isSelectable
                            ? "text-[16px] font-bold text-dark-80"
                            : isStatic
                                ? "text-[16px] font-bold text-dark-80"
                                : "text-[14px] font-medium text-light-80 mt-1"
                    }
                >
                    {title}
                </h3>
            </div>

            {!isStatic && !isSelectable && (
                <div className="mt-3">
                    <DefaultButton
                        label={buttonLabel}
                        onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            onButtonClick();
                        }}
                        variant="primary"
                        size="small"
                        disabled={isButtonDisabled}
                        className="!px-4"
                    />
                </div>
            )}

                <div
                    className={`absolute bottom-0 left-0 h-[11px] w-full rounded-b-md ${accentColorClass}`}
                />
        </div>
    );
};

export default React.memo(Card);